import { useState, useEffect } from "react";
import { useFormik } from "formik";
import { Link, useNavigate } from "react-router-dom";
import Cleave from "cleave.js/react";
import * as Yup from "yup";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Form,
  FormFeedback,
  Alert,
  Spinner,
} from "reactstrap";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import { loginReq, verifyOtp } from "../../api/auth";
import { getDeviceInfo } from "../../utils/device";

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [passwordShow, setPasswordShow] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [requestId, setRequestId] = useState(null);
  const [device, setDevice] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    getDeviceInfo().then(setDevice);
  }, []);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      login: "",
      password: "",
    },
    validationSchema: Yup.object({
      login: Yup.string()
        .matches(/^998[0-9]{9}$/, "Введите действительный узбекский номер телефона")
        .required("Пожалуйста, введите ваш номер телефона"),
      password: Yup.string().required("Пожалуйста, введите ваш пароль"),
    }),
    onSubmit: async (values) => {
      if (!device) return;
      setLoading(true);
      setError(null);
      try {
        const res = await loginReq({ ...values, device });
        if (res?.data?.accessToken) {
          localStorage.setItem("token", res.data.accessToken);
          navigate("/");
        } else if (res?.data?.requestId) {
          setRequestId(res.data.requestId);
          setShowOtp(true);
        } else {
          setError("Неожиданный ответ от сервера");
        }
      } catch (err) {
        setError(err?.data?.error?.message || "Ошибка входа");
      } finally {
        setLoading(false);
      }
    },
  });

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (!device) return;
    setLoading(true);
    setError(null);
    try {
      const res = await verifyOtp({
        requestId,
        code: otpCode,
        device,
      });
      if (res?.data?.accessToken) {
        localStorage.setItem("token", res.data.accessToken);
        navigate("/");
      } else {
        setError("Неверный OTP или ошибка сервера");
      }
    } catch (err) {
      setError(err?.data?.error?.message || "Ошибка проверки OTP");
    } finally {
      setLoading(false);
    }
  };

  document.title = "Вход | Bazaar";

  return (
    <ParticlesAuth>
      <div className="auth-page-content mt-lg-5">
        <Container>
          <Row
            className="justify-content-center"
            style={{ marginTop: "140px" }}
          >
            <Col md={8} lg={6} xl={5}>
              <Card className="mt-4">
                <CardBody className="p-4">
                  <div className="text-center mt-2 mb-4">
                    <h5 className="text-primary">Добро пожаловать!</h5>
                  </div>
                  {error && <Alert color="danger">{error}</Alert>}

                  {!showOtp ? (
                    <Form onSubmit={validation.handleSubmit}>
                      <div className="mb-3">
                        <label htmlFor="cleave-phone" className="form-label">
                          Телефон
                        </label>
                        <Cleave
                          name="login"
                          placeholder="998xxxxxxxxx"
                          value={validation.values.login}
                          onChange={validation.handleChange}
                          className="form-control"
                          options={{ numericOnly: true, blocks: [12] }}
                        />
                        {validation.errors.login && (
                          <FormFeedback type="invalid" className="d-block">
                            {validation.errors.login}
                          </FormFeedback>
                        )}
                      </div>
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="password-input">
                          Пароль
                        </Label>
                        <div className="position-relative auth-pass-inputgroup mb-3">
                          <Input
                            name="password"
                            value={validation.values.password}
                            type={passwordShow ? "text" : "password"}
                            className="form-control pe-5"
                            placeholder="Введите пароль"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={!!validation.errors.password}
                          />
                          {validation.errors.password && (
                            <FormFeedback type="invalid">
                              {validation.errors.password}
                            </FormFeedback>
                          )}
                          <button
                            className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                            type="button"
                            onClick={() => setPasswordShow(!passwordShow)}
                          >
                            <i className="ri-eye-fill align-middle"></i>
                          </button>
                        </div>
                      </div>
                      <div className="mt-4">
                        <Button
                          color="success"
                          disabled={loading || !device}
                          className="btn btn-success w-100"
                          type="submit"
                        >
                          {loading ? (
                            <Spinner size="sm" className="me-2">
                              Загрузка...
                            </Spinner>
                          ) : (
                            "Войти"
                          )}
                        </Button>
                      </div>
                    </Form>
                  ) : (
                    <Form onSubmit={handleOtpSubmit}>
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="otp-code">
                          Введите код OTP
                        </Label>
                        <Input
                          id="otp-code"
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value)}
                          className="form-control"
                          required
                        />
                      </div>
                      <div className="mt-4">
                        <Button
                          color="primary"
                          disabled={loading || !device}
                          className="w-100"
                          type="submit"
                        >
                          {loading ? (
                            <Spinner size="sm" className="me-2">
                              Проверка...
                            </Spinner>
                          ) : (
                            "Подтвердить OTP"
                          )}
                        </Button>
                      </div>
                    </Form>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </ParticlesAuth>
  );
};

export default Login;
